export function toDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatDate(value) {
  const date = toDate(value);
  if (!date) return '—';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function money(value) {
  return Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function financialYearForDate(value = new Date()) {
  const date = toDate(value) || new Date();
  const year = date.getFullYear();
  const start = date.getMonth() < 3 ? year - 1 : year;
  return `${String(start).slice(-2)}-${String(start + 1).slice(-2)}`;
}

export function computeLineTotals(lines = [], gstApplicable = false, gstRate = 18) {
  const taxableAmount = lines.reduce((sum, line) => sum + Number(line.amount || 0), 0);
  const gstAmount = gstApplicable ? taxableAmount * Number(gstRate || 0) / 100 : 0;
  return {
    taxableAmount,
    gstAmount,
    grandTotal: taxableAmount + gstAmount,
  };
}

export function paymentTotal(payments = []) {
  return payments.reduce((sum, item) => sum + Number(item.amount || 0), 0);
}

export function outstandingOf(proforma) {
  return Math.max(0, Number(proforma?.grandTotal || 0) - paymentTotal(proforma?.payments));
}

export function paymentStatusOf(proforma) {
  const paid = paymentTotal(proforma?.payments);
  const total = Number(proforma?.grandTotal || 0);
  if (total > 0 && paid >= total - 0.01) return 'Paid';
  if (paid > 0) return 'Partially Paid';
  return 'Unpaid';
}

export function addressOf(client = {}) {
  return [client.address1, client.address2, client.city, client.state, client.pinCode]
    .filter(Boolean)
    .join(', ');
}

export function assignmentDescription(assignment = {}) {
  return [assignment.assignmentType || assignment.category, assignment.financialYear, assignment.period]
    .filter(Boolean)
    .join(' - ');
}

export function daysSince(value, reference = new Date()) {
  const date = toDate(value);
  if (!date) return null;
  const start = new Date(date); start.setHours(0, 0, 0, 0);
  const end = toDate(reference) || new Date(); end.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((end.getTime() - start.getTime()) / 86400000));
}

export function ageingBucket(value, reference = new Date()) {
  const days = daysSince(value, reference);
  if (days == null) return 'Unknown';
  if (days <= 15) return '0–15 days';
  if (days <= 30) return '16–30 days';
  if (days <= 60) return '31–60 days';
  if (days <= 90) return '61–90 days';
  return '90+ days';
}

export function latestFollowUp(proforma = {}) {
  const entries = Array.isArray(proforma.followUps) ? proforma.followUps : [];
  return entries.length ? entries[entries.length - 1] : null;
}

export function nextFollowUpOf(proforma = {}) {
  return proforma.nextFollowUpDate || latestFollowUp(proforma)?.nextFollowUpDate || '';
}

export function isCompletedWork(row = {}) {
  const status = String(row.status || '').trim().toLowerCase();
  return ['completed', 'complete', 'done', 'closed'].includes(status);
}

export function completedDateOf(row = {}) {
  return row.completedAt || row.completedOn || row.updatedAt || null;
}

export function billingDocumentLabel(proforma = {}) {
  const companyName = String(proforma.companyName || proforma.companyLegalName || "").trim().toLowerCase();
  return companyName.includes("taxempt") ? "Invoice" : "Proforma Invoice";
}
